import React, { useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetLpnNumbersQuery, useCreateLpnNumbersMutation } from '@/redux/slices/lpn/lpnApiSlice';
import { PalletType, LpnNumber } from '@/redux/slices/pallet/palletSlice';
import AlertTooltip from './alert-tooltip';
import { showSuccessToast, showErrorToast } from "./toast/toast";
import { useValidatePalletConfigQuery } from '@/redux/slices/define/defineApiSlice';
interface LpnTableProps {
  pallet: PalletType;
  projectId?: string;
  onFormChange: () => void;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
  getLpnNumbers?: () => Promise<void>;
  setDataSaving: (dataSaving: boolean) => void;
}

const statusType = [{
  label: "Pre-Ship",
  value: "Pre Ship"
}, {
  label: "Post-Ship",
  value: "Post Ship"
},
{ label: "Stack start(stacked+30m)", value: "Stack start(stacked+30m)" },
{ label: "Stack Week-1", value: "Stack Week-1" },
{ label: "Stack Week-2", value: "Stack Week-2" },
{ label: "Stack Week-3", value: "Stack Week-3" },
{ label: "Stack Week-4 (unstacking+30M)", value: "Stack Week-4 (unstacking+30M)" }
]

const validationSchema = Yup.object().shape({
  lpnNumbers: Yup.array().of(
    Yup.object().shape({
      // lpnNo: Yup.string().required('LPN Number is required'),
      endStatusPallet: Yup.string().required('End Status is required'),
      rqlState: Yup.string().required('RQL State is required'),
    })
  ),
});

interface FormValues {
  lpnNumbers: LpnNumber[];
}

export default function LpnTable({
  pallet,
  projectId,
  onFormChange,
  isOffline,
  onOfflineSubmit,
  getLpnNumbers,
  setDataSaving
}: LpnTableProps) {
  const { data: lpnData, isLoading, isFetching } = useGetLpnNumbersQuery(pallet.id, {
    skip: !pallet.id || isOffline,
    refetchOnMountOrArgChange: true
  });
  const [createLpnNumbers] = useCreateLpnNumbersMutation();
  console.log(lpnData, 'lpn data')
  const formik = useFormik<FormValues>({
    initialValues: {
      lpnNumbers: []
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isOffline && onOfflineSubmit) {
          await onOfflineSubmit({
            type: 'lpnNumbers',
            data: values
          });
          showSuccessToast("LPN Numbers saved offline");
          return;
        }

        await createLpnNumbers({
          palletId: Number(pallet.id),
          lpnNumbers: values.lpnNumbers
        }).unwrap();
        console.log({
          palletId: Number(pallet.id),
          lpnNumbers: values.lpnNumbers
        }, 'create lpn number')
        showSuccessToast("LPN Numbers have been saved successfully");
      } catch (error) {
        console.error('Error saving LPN numbers:', error);
        showErrorToast("Failed to save LPN numbers");
      }
    },
    enableReinitialize: true,
  });
  useEffect(() => {
    setDataSaving(formik.isSubmitting);
  }, [formik.isSubmitting, setDataSaving]);
  // Handle online data
  useEffect(() => {
    if (!isOffline && lpnData?.lpnNumbers && !isLoading && !isFetching) {
      const existingLpnNumbers = lpnData.lpnNumbers as LpnNumber[];
      const palletsToAdd = Number(pallet.numberOfPallets) - existingLpnNumbers.length;
      if (palletsToAdd === 0) {
        formik.setValues({ lpnNumbers: existingLpnNumbers }, false);
      } else {
        const updatedLpnNumbers = [
          ...existingLpnNumbers,
          ...Array.from({ length: palletsToAdd }, () => ({
            lpnNo: '',
            endStatusPallet: 'Stack Week-4 (unstacking+30M)',
            rqlState: 'no',
          }))
        ];
        formik.setValues({ lpnNumbers: updatedLpnNumbers }, false);
      }
    } else if (!isOffline && !isLoading && !isFetching) {
      const updatedLpnNumbers = Array.from({ length: Number(pallet.numberOfPallets) }, () => ({
        lpnNo: '',
        endStatusPallet: 'Stack Week-4 (unstacking+30M)',
        rqlState: 'no',
      }));
      formik.setValues({ lpnNumbers: updatedLpnNumbers }, false);
    }
  }, [lpnData, pallet.numberOfPallets, isLoading, isFetching, isOffline]);

  // Handle offline data
  useEffect(() => {
    if (isOffline && getLpnNumbers) {
      const fetchOfflineData = async () => {
        try {
          const offlineData = await getLpnNumbers();
          if (offlineData) {
            const existingLpnNumbers = offlineData as LpnNumber[];
            const palletsToAdd = Number(pallet.numberOfPallets) - existingLpnNumbers.length;
            if (palletsToAdd === 0) {
              formik.setValues({ lpnNumbers: existingLpnNumbers }, false);
            } else {
              const updatedLpnNumbers = [
                ...existingLpnNumbers,
                ...Array.from({ length: palletsToAdd }, () => ({
                  lpnNo: '',
                  endStatusPallet: 'Stack Week-4 (unstacking+30M)',
                  rqlState: 'no',
                }))
              ];
              formik.setValues({ lpnNumbers: updatedLpnNumbers }, false);
            }
          } else {
            const updatedLpnNumbers = Array.from({ length: Number(pallet.numberOfPallets) }, () => ({
              lpnNo: '',
              endStatusPallet: 'Stack Week-4 (unstacking+30M)',
              rqlState: 'no',
            }));
            formik.setValues({ lpnNumbers: updatedLpnNumbers }, false);
          }
        } catch (error) {
          console.error('Error fetching offline LPN numbers:', error);
        }
      };
      fetchOfflineData();
    }
  }, [isOffline, pallet.numberOfPallets, getLpnNumbers]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, index: number, field: string) => {
    const newLpnNumbers = [...formik.values.lpnNumbers];
    newLpnNumbers[index] = { ...newLpnNumbers[index], [field]: e.target.value };
    formik.setValues({ lpnNumbers: newLpnNumbers }, true);
    onFormChange();
  }, [formik, onFormChange]);

  const handleSelectChange = useCallback((value: string, index: number, field: string) => {
    const newLpnNumbers = [...formik.values.lpnNumbers];
    newLpnNumbers[index] = { ...newLpnNumbers[index], [field]: value };
    formik.setValues({ lpnNumbers: newLpnNumbers }, true);
    onFormChange();
  }, [formik, onFormChange]);

  // Add keyboard navigation handler to move focus with arrow keys
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
    let targetRow = rowIndex;
    let targetCol = colIndex;
    switch (e.key) {
      case 'ArrowUp':
        targetRow = Math.max(0, rowIndex - 1);
        break;
      case 'ArrowDown':
        targetRow = Math.min(formik.values.lpnNumbers.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        targetCol = Math.max(1, colIndex - 1);
        break;
      case 'ArrowRight':
        targetCol = Math.min(3, colIndex + 1);
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextEl = document.querySelector(`[data-row="${targetRow}"][data-col="${targetCol}"]`) as HTMLElement | null;
    nextEl?.focus();
  }, [formik.values.lpnNumbers.length]);

  return (
    <form id={`lpn-table-${pallet.id}`} onSubmit={formik.handleSubmit}>
      <div className="w-full">
        <h3 className="text-lg text-black font-semibold mb-4">
          Pallet Identifiers for {pallet.variant} {isOffline ? '(Offline)' : ''}
        </h3>
        <Table className="border-collapse">
          <TableHeader>
            <TableRow>
              {["Pallet Number", "Pallet Identifier", "End Status", "RQL State (yes or no)"].map((head, headIndex) => (
                <TableHead
                  key={`${headIndex}-${head}`}
                  className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left max-w-[200px]"
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {formik.values.lpnNumbers.map((lpn, index) => (
              <TableRow key={index}>
                <TableCell className="border border-gray-300 bg-states-disabled font-semibold px-3">
                  {index + 1}
                </TableCell>
                <TableCell className="border border-gray-300 p-0">
                  <div className="relative">
                    <Input
                      data-row={index} data-col={1}
                      onKeyDown={(e) => handleKeyDown(e, index, 1)}
                      className="w-full h-9 border-none"
                      placeholder="Enter LPN Number"
                      value={lpn.lpnNo}
                      onChange={(e) => handleInputChange(e, index, 'lpnNo')}
                    />
                    {formik.touched.lpnNumbers?.[index]?.lpnNo &&
                      formik.errors.lpnNumbers?.[index] &&
                      typeof formik.errors.lpnNumbers[index] !== 'string' &&
                      formik.errors.lpnNumbers[index]?.lpnNo && (
                        <AlertTooltip message={formik.errors.lpnNumbers[index]?.lpnNo as string} />
                      )}
                  </div>
                </TableCell>
                <TableCell className="border border-gray-300 p-0">
                  <div className="relative">
                    <Select
                      value={lpn.endStatusPallet}
                      onValueChange={(value) => handleSelectChange(value, index, 'endStatusPallet')}
                    >
                      <SelectTrigger
                        data-row={index} data-col={2}
                        onKeyDown={(e) => handleKeyDown(e, index, 2)}
                        className="w-full rounded-none bg-transparent border-2 border-required focus:border-blue"
                      >
                        <SelectValue placeholder="Select End Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {statusType.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formik.touched.lpnNumbers?.[index]?.endStatusPallet &&
                      formik.errors.lpnNumbers?.[index] &&
                      typeof formik.errors.lpnNumbers[index] !== 'string' &&
                      formik.errors.lpnNumbers[index]?.endStatusPallet && (
                        <AlertTooltip message={formik.errors.lpnNumbers[index]?.endStatusPallet as string} />
                      )}
                  </div>
                </TableCell>
                <TableCell className="border border-gray-300 p-0">
                  <div className="relative">
                    <Select
                      value={lpn.rqlState}
                      onValueChange={(value) => handleSelectChange(value, index, 'rqlState')}
                    >
                      <SelectTrigger
                        data-row={index} data-col={3}
                        onKeyDown={(e) => handleKeyDown(e, index, 3)}
                        className="w-full rounded-none bg-transparent border-2 border-required focus:border-blue"
                      >
                        <SelectValue placeholder="Select RQL State" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                    {formik.touched.lpnNumbers?.[index]?.rqlState &&
                      formik.errors.lpnNumbers?.[index] &&
                      typeof formik.errors.lpnNumbers[index] !== 'string' &&
                      formik.errors.lpnNumbers[index]?.rqlState && (
                        <AlertTooltip message={formik.errors.lpnNumbers[index]?.rqlState as string} />
                      )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <button
        type="submit"
        style={{ display: 'none' }}
        aria-hidden="true"
        title="Submit LPN Numbers"
      >
        Submit LPN Numbers
      </button>
    </form>
  );
}
